import React from 'react';
import { Handle, Position } from 'reactflow';
import { cn } from '../../lib/utils';
import { stepDefinition, TONE_CLASSES } from './stepRegistry';

/** Node ids the builder generates (`http-1712345678901`) say nothing; only custom labels are shown. */
const AUTO_LABEL_RE = /^[a-z-]+-\d{10,}$/;

export function customLabel(label: unknown): string | null {
  const text = typeof label === 'string' ? label.trim() : '';
  return text && !AUTO_LABEL_RE.test(text) ? text : null;
}

export function shortUrl(url: string | undefined): string | null {
  if (!url) return null;
  const bare = url.replace(/^https?:\/\//, '');
  return bare.length > 34 ? `${bare.slice(0, 33)}…` : bare;
}

export interface NodeLine {
  text: React.ReactNode;
  mono?: boolean;
}

/**
 * Card shared by every step on the canvas: icon tile and colour from the step registry, the
 * custom label (or the step's name) as title, and one or two lines of its key configuration.
 */
export function BaseNode({
  type,
  label,
  alias,
  lines = [],
  badge,
  hasTarget = true,
  hasSource = true,
  footer,
  children,
}: {
  type: string;
  label?: unknown;
  alias?: string;
  lines?: (NodeLine | null | undefined | false | '')[];
  badge?: React.ReactNode;
  hasTarget?: boolean;
  hasSource?: boolean;
  footer?: React.ReactNode;
  children?: React.ReactNode;
}) {
  const step = stepDefinition(type);
  const Icon = step?.icon;
  const tone = TONE_CLASSES[step?.tone ?? 'slate'];
  const custom = customLabel(label);
  const title = custom ?? step?.label ?? type;
  const visibleLines = lines.filter(
    (l): l is NodeLine => !!l && l.text !== undefined && l.text !== null && l.text !== '',
  );

  return (
    <div
      className="w-[200px] rounded-xl border border-border bg-card text-card-foreground shadow-sm transition-shadow hover:shadow-md"
      title={custom ? step?.label ?? type : undefined}
    >
      {hasTarget && <Handle type="target" position={Position.Top} />}
      <div className="flex items-start gap-2 p-2">
        <div className={cn('flex h-7 w-7 shrink-0 items-center justify-center rounded-lg', tone.tile)}>
          {Icon && <Icon className="h-4 w-4" />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="truncate text-[13px] font-medium leading-[18px]">{title}</span>
            {badge}
          </div>
          {visibleLines.map((line, i) => (
            <div
              key={i}
              className={cn(
                'truncate text-[11px] leading-4 text-muted-foreground',
                line.mono && 'font-mono',
              )}
            >
              {line.text}
            </div>
          ))}
          {alias && (
            <div className="truncate font-mono text-[11px] leading-4 text-muted-foreground/70">{`{{${alias}}}`}</div>
          )}
        </div>
      </div>
      {footer && <div className="border-t border-border px-2 py-1 text-[11px] text-muted-foreground">{footer}</div>}
      {hasSource && <Handle type="source" position={Position.Bottom} />}
      {children}
    </div>
  );
}

/** Small mono chip next to the title, e.g. the HTTP method. */
export function NodeBadge({ children }: { children: React.ReactNode }) {
  return (
    <span className="shrink-0 rounded bg-muted px-1 font-mono text-[10px] font-semibold leading-4 text-muted-foreground">
      {children}
    </span>
  );
}
